import { useQuery } from '@apollo/client';

import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import SuspenseQuery from 'components/SuspenseQuery';
import OBLoaderPage from 'features/Onboarding/components/OBLoaderPage/OBLoaderPage';

import { GetCompanyComplianceDataDocument } from './graphql/queries/getCompanyComplianceData.gql';
import machineConfig, { model, State } from './machine';
import { Model } from './machine/model';
import CompanyActivityOutsideEU from './pages/CompanyActivityOutsideEU';
import CompanyHasSanctions from './pages/CompanyHasSanctions';
import CompanyMemberSanctions from './pages/CompanyMemberSanctions';
import CompanySanctions from './pages/CompanySanctions';

interface CompanyComplianceProps {
  companyProfileId: string;
  /**
   * If true, the locales will be adapted for a mandatee
   */
  isMandatee?: boolean;
}

const CompanyComplianceComponent = withStateMachine<Model>(
  ({ machine, moduleData }) => {
    const [state, send] = machine;

    const stringifiedState = stringifyState<State>(state);

    switch (stringifiedState) {
      case State.ACTIVITY_OUTSIDE_EU:
      case State.SAVE_ACTIVITY_OUTSIDE_EU:
        return (
          <CompanyActivityOutsideEU
            companyProfile={moduleData.viewer.company.profile}
            isMandatee={moduleData.isMandatee}
            isSaving={state.matches(State.SAVE_ACTIVITY_OUTSIDE_EU)}
            onPrev={() => send(model.events.PREV())}
            onSubmit={(values) =>
              send(model.events.SUBMIT_ACTIVITY_OUTSIDE_EU(values))
            }
          />
        );
      case State.COMPANY_MEMBER_SANCTIONS:
      case State.SAVE_COMPANY_MEMBER_SANCTIONS:
        return (
          <CompanyMemberSanctions
            companyProfile={moduleData.viewer.company.profile}
            isMandatee={moduleData.isMandatee}
            isSaving={state.matches(State.SAVE_COMPANY_MEMBER_SANCTIONS)}
            onPrev={() => send(model.events.PREV())}
            onSave={(values) =>
              send(model.events.SUBMIT_COMPANY_MEMBER_SANCTIONS(values))
            }
          />
        );

      case State.COMPANY_SANCTIONS:
      case State.SAVE_COMPANY_SANCTIONS:
        return (
          <CompanySanctions
            companyProfile={moduleData.viewer.company.profile}
            isMandatee={moduleData.isMandatee}
            isSaving={state.matches(State.SAVE_COMPANY_SANCTIONS)}
            onPrev={() => send(model.events.PREV())}
            onSave={(values) =>
              send(model.events.SUBMIT_COMPANY_SANCTIONS(values))
            }
          />
        );

      case State.INELIGIBLE:
        return (
          <CompanyHasSanctions
            isMandatee={moduleData.isMandatee}
            onPrev={() => send(model.events.PREV())}
          />
        );

      case State.ABORT:
      case State.DONE:
        return <OBLoaderPage onPrev={() => null} />;

      default:
        return assertNever(stringifiedState);
    }
  },

  {
    devTools: true,
    machineConfig,
  },
);

const CompanyCompliance = asBentoModule<CompanyComplianceProps>(
  ({ companyProfileId, isMandatee = false, onDone }) => {
    const query = useQuery(GetCompanyComplianceDataDocument, {
      variables: {
        companyProfileId,
      },
    });

    return (
      <SuspenseQuery
        loader={<OBLoaderPage onPrev={() => null} />}
        query={query}
      >
        {(moduleData) => (
          <CompanyComplianceComponent
            moduleData={{ ...moduleData, isMandatee }}
            onDone={onDone}
          />
        )}
      </SuspenseQuery>
    );
  },
);

export default CompanyCompliance;
